import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	getEntityById,
	getStatusLabelFromPipelineFilters,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	applyChangesToACCRun,
	cycleRunsRestCrudThunksForBuyer,
	cycleRunsRestCrudThunksForSupplier,
	getACCRunApplyChangesStatus,
} from '../../thunks/cycle_runs_thunks';
import { Button, message, Modal } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import {
	accAuditsRestCrudThunksForBuyer,
	accAuditsRestCrudThunksForSupplier,
} from '../../thunks/acc_audits_thunks';
import OWPoll from '../common/OWPoll';
import { ROLE_TYPES } from '../../utils/DataConstants';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import CycleRunPipeLineFilters from '../asset_cycle_runs_detail_details_page/CycleRunPipeLineFilters';
import { ACC_RUN_STATUSES, getCycleRunCounts } from '../../utils/AssetCycleUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { withUserType } from '../withUserType';

const ACC_AUDITS_TARGET_COLLECTION = 'accAuditsIndex';

const ACCRunDetailPage: FC<any> = ({
	match,
	companyConfig,
	getCycleRun,
	childRoutes,
	cycleRun,
	accAudits,
	fetchACCAudits,
	getApplyChangeStatus,
	applyChangesToRun,
	userType,
}): React.ReactElement => {
	const [applyingChanges, setApplyingChanges] = useState(false);
	const [applyChangeInitiated, setApplyChangeInitiated] = useState(false);

	const cycleRunId = useMemo(() => match.params.id, [match.params.id]);

	const defaultPath = `/${userType}/admin/cycleevents`;

	const refreshCycleRun = useCallback(() => getCycleRun(cycleRunId), [cycleRunId, getCycleRun]);

	useEffect(() => {
		refreshCycleRun();
	}, [refreshCycleRun]);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${ACC_AUDITS_TARGET_COLLECTION}.${propName}`, accAudits, {}),
		[accAudits]
	);

	const refreshAudits = useCallback(() => {
		fetchACCAudits(
			{},
			ACC_AUDITS_TARGET_COLLECTION,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchACCAudits, getStateSliceProp]);

	const onApplyChangeClick = useCallback(() => {
		setApplyingChanges(true);
		applyChangesToRun(cycleRunId)
			.then(() => {
				setApplyChangeInitiated(true);
				message.success('Apply changes initiated successfully!');
			})
			.catch(message.error)
			.finally(() => setApplyingChanges(false));
	}, [applyChangesToRun, cycleRunId]);

	const onChangesApplySuccess = useCallback(() => {
		refreshAudits();
		refreshCycleRun();
	}, [refreshAudits, refreshCycleRun]);

	const { totalAssets, assetsLogged, newAssetAdded, transferCount } = getCycleRunCounts(cycleRun);
	const approvedAuditCount = nullSafeGetOrElse('approvedAuditCount', cycleRun, 0);

	const showApplyChangesConfirm = useCallback(() => {
		Modal.confirm({
			title: (
				<div className="h5medium">
					Apply Changes to '<CheckCircleFilled style={{ color: '#237804' }} /> Approved' Assets?
				</div>
			),
			content: (
				<div className="bodyregular mt-2 text-character-primary">
					This will update {approvedAuditCount} asset
					{approvedAuditCount !== 1 ? 's' : ''}. Once applied, you won't be able to edit them from
					this screen.
				</div>
			),
			okText: 'Apply Changes',
			cancelText: 'Cancel',
			onOk: onApplyChangeClick,
		});
	}, [approvedAuditCount, onApplyChangeClick]);

	return (
		<div>
			<DetailPageHeader
				exactPath="/buyer/assetCycleRuns/detail/:id"
				redirectPath={`/buyer/assetCycleRuns/detail/${cycleRunId}/details`}
				backLinkText={'Back to Asset Cycle Events'}
				backLinkTo={defaultPath}
				title={nullSafeGet('location.name', cycleRun) || ''}
				subtitle={
					<div className="flex flex-col items-start gap-2 text-white">
						<div className="flex flex-row items-center gap-2">
							<GeneralStatusDisplay
								statusKey={nullSafeGet('status', cycleRun)}
								statusLabel={getStatusLabelFromPipelineFilters(
									CycleRunPipeLineFilters,
									nullSafeGet('status', cycleRun)
								)}
								className="footnoteDescription"
							/>

							{[
								ACC_RUN_STATUSES.READY_FOR_APPROVAL,
								ACC_RUN_STATUSES.CHANGES_APPLIED,
								ACC_RUN_STATUSES.APPROVED,
							].includes(nullSafeGet('status', cycleRun)) && cycleRun.completedAt ? (
								<div className="footnoteregular text-white">
									Completed:{' '}
									<DateTimeHover showDate timestamp={cycleRun.completedAt} showTime={true} />
								</div>
							) : null}
							{nullSafeGet('status', cycleRun) === ACC_RUN_STATUSES.IN_PROGRESS &&
							cycleRun.startedAt ? (
								<div className="footnoteregular text-white">
									Started: <DateTimeHover showDate timestamp={cycleRun.startedAt} showTime={true} />
								</div>
							) : null}
						</div>
						<div className="flex flex-row items-center gap-1">
							<span className="bodyregular">
								Assets Logged:{' '}
								<span className="bodymedium">
									{assetsLogged}/{totalAssets}
									{(transferCount > 0 || newAssetAdded > 0) && (
										<>
											{'['}
											{transferCount > 0 &&
												`${transferCount} transfer${transferCount !== 1 ? 's' : ''}`}
											{transferCount > 0 && newAssetAdded > 0 && ', '}
											{newAssetAdded > 0 && `${newAssetAdded} new`}
											{']'}
										</>
									)}
								</span>
							</span>
						</div>
					</div>
				}
				actions={
					<OWPoll
						key="apply-changes-to-acc-run"
						id="apply-changes-to-acc-run"
						fetchStatusPromises={[getApplyChangeStatus]}
						successMessage="Changes applied successfully!"
						message="Change apply in progress!"
						onRefresh={onChangesApplySuccess}
						successMessageMandatory={applyChangeInitiated}
						renderElementWithProps={(props) => (
							<Button
								ghost
								type="ghost"
								className="inline-block-visible-md ml-2"
								loading={applyingChanges}
								{...props}
								disabled={approvedAuditCount === 0 || props.disabled}
								onClick={showApplyChangesConfirm}
							>
								Apply Changes ({approvedAuditCount})
							</Button>
						)}
					/>
				}
				tabs={[]}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	cycleRun: getEntityById(ownProps.match.params.id, state.cycle_runs),
	companyConfig: state.company_config,
	accAudits: state.acc_audits,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getCycleRun: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleRunsRestCrudThunksForSupplier.readOne(id)
				: cycleRunsRestCrudThunksForBuyer.readOne(id)
		),
	fetchACCAudits: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? accAuditsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: accAuditsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	getApplyChangeStatus: () => dispatch(getACCRunApplyChangesStatus(ownProps.userType)()),
	applyChangesToRun: (runId) => dispatch(applyChangesToACCRun(ownProps.userType)(runId)),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(ACCRunDetailPage as any) as any)
);
